import React, { useState } from 'react';
import './EmailCollectionModal.css';
import { saveEmailToDatabase, signInWithGoogle, sendSignInLink } from '../services/firebase';
import { googleIconBase64 } from '../assets/google-icon'; // Update this import

function EmailCollectionModal({ onClose, onSubmit, isMandatory = false }) {
  const [email, setEmail] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showOptionalFields, setShowOptionalFields] = useState(false);
  const [name, setName] = useState('');
  const [amazonStore, setAmazonStore] = useState('');
  const [authSent, setAuthSent] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Basic email validation
    if (!email || !/\S+@\S+\.\S+/.test(email)) {
      setError('Please enter a valid email address');
      return;
    }
    
    setLoading(true);
    setError('');
    
    try {
      // Send sign-in link to email
      await sendSignInLink(email);
      
      // Save email to database
      await saveEmailToDatabase({
        email,
        name: name || null,
        amazonStore: amazonStore || null,
        source: 'mandatory_collection',
        timestamp: new Date().toISOString()
      });
      
      // Update UI to show that auth link was sent
      setAuthSent(true);
      
      // Call the onSubmit callback with the email and optional fields
      if (onSubmit) {
        await onSubmit(email, name, amazonStore);
      }
    } catch (err) {
      console.error('Error in email submission:', err);
      setError('There was an error. Please try again later or contact support.');
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleSignIn = async () => {
    try {
      setLoading(true);
      setError('');
      
      // Sign in with Google
      const user = await signInWithGoogle();
      
      // If successful, save the email to database
      if (user && user.email) {
        await saveEmailToDatabase({
          email: user.email,
          name: user.displayName || null,
          source: 'google_signin',
          timestamp: new Date().toISOString()
        });
        
        // Call the onSubmit callback with the user's email
        if (onSubmit) {
          await onSubmit(user.email, user.displayName || '');
        }
        
        // Close the modal
        onClose();
      }
    } catch (err) {
      console.error('Error with Google sign-in:', err);
      setError(err.message || 'Google sign-in failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="email-modal-overlay">
      <div className="email-modal">
        {!isMandatory && (
          <button className="close-button" onClick={onClose}>×</button>
        )}
        
        <h2>{isMandatory ? 'Email Required' : 'Get Accurate Removal Fee Estimates'}</h2>
        
        {authSent ? (
          <div className="auth-sent-message">
            <h3>Check Your Email!</h3>
            <p>
              We've sent a sign-in link to <strong>{email}</strong>. 
              Click the link in your email to sign in and continue using the calculator.
            </p>
            <p>
              You can close this window and continue using the example calculation 
              until you verify your email.
            </p>
            <button 
              className="close-button-large"
              onClick={onClose}
            >
              Continue with Example
            </button>
          </div>
        ) : (
          <>
            <p>
              {isMandatory 
                ? 'Want to be the first to know about our next game-changing tool? Just drop your email below '
                : ''}
            </p>
            
            <button 
              className="google-signin-button"
              onClick={handleGoogleSignIn}
              disabled={loading}
            >
              <img src={googleIconBase64} alt="Google" className="google-icon" />
              Sign in with Google
            </button>
            
            <div className="divider">
              <span>or</span>
            </div>
            
            <form onSubmit={handleSubmit}>
              <div className="form-group">
                <label htmlFor="email">Email Address*</label>
                <input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="your@email.com"
                  required
                />
              </div>
              
              {showOptionalFields ? (
                <>
                  <div className="form-group">
                    <label htmlFor="name">Your Name (Optional)</label>
                    <input
                      id="name"
                      type="text"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      placeholder="Your Name"
                    />
                  </div>
                  
                  <div className="form-group">
                    <label htmlFor="amazonStore">Amazon Store Name (Optional)</label>
                    <input
                      id="amazonStore"
                      type="text"
                      value={amazonStore}
                      onChange={(e) => setAmazonStore(e.target.value)}
                      placeholder="Your Amazon Store Name"
                    />
                  </div>
                </>
              ) : (
                <button 
                  type="button" 
                  className="show-more-button"
                  onClick={() => setShowOptionalFields(true)}
                >
                  + Add more details (optional)
                </button>
              )}
              
              {error && <div className="error-message">{error}</div>}
              
              <div className="modal-buttons">
                <button 
                  type="submit" 
                  className="submit-button"
                  disabled={loading}
                >
                  {loading ? 'Processing...' : 'Continue to Calculator'}
                </button>
                
                {isMandatory && (
                  <button 
                    type="button" 
                    className="back-to-example-button"
                    onClick={onClose}
                  >
                    Back to Example Calculation
                  </button>
                )}
              </div>
            </form>
            
            <p className="privacy-note">
              We respect your privacy. Your information will never be shared with third parties.
            </p>
          </>
        )}
      </div>
    </div>
  );
}

export default EmailCollectionModal; 